import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon, type IconName } from '../components/icons';
import { PlanBadge } from '../features/billing/PlanBadge';
import { contactDisplayName } from '../features/contacts/shared';
import { automationsApi, contactsApi, type PlanFeatureKey } from '../lib/api';
import { useBodyScrollLock, useFocusTrap } from '../lib/overlay';
import { ALL_PAGES, MAIN_PAGES, SETTINGS_PAGES } from './nav';

/**
 * ⌘K search (refonte): jump to a page, an automation or a contact. Opened by
 * ⌘K / Ctrl+K anywhere in the shell, or by the search field in the sidebar
 * and the search button in the phone top bar.
 */

interface PaletteState {
  open: () => void;
}

const PaletteContext = createContext<PaletteState | null>(null);

export function useCommandPalette(): PaletteState {
  const ctx = useContext(PaletteContext);
  if (!ctx) throw new Error('useCommandPalette must be used inside <CommandPaletteProvider>');
  return ctx;
}

/** The shortcut as this platform writes it, for the hint beside the search field. */
export function shortcutLabel(): string {
  const platform = typeof navigator === 'undefined' ? '' : navigator.platform;
  return /Mac|iPhone|iPad/.test(platform) ? '⌘K' : 'Ctrl K';
}

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo(() => ({ open }), [open]);
  return (
    <PaletteContext.Provider value={value}>
      {children}
      {isOpen ? <CommandPalette onClose={close} /> : null}
    </PaletteContext.Provider>
  );
}

type Section = 'sectionPages' | 'sectionAutomations' | 'sectionContacts';

interface Result {
  id: string;
  section: Section;
  label: string;
  hint?: string;
  icon: IconName;
  to: string;
  /** A page the plan may turn off: its row carries the plan's name (D-183). */
  feature?: PlanFeatureKey;
}

const SECTIONS: Section[] = ['sectionPages', 'sectionAutomations', 'sectionContacts'];
const CONTACT_MIN_QUERY = 2;
const CONTACT_DEBOUNCE_MS = 200;
const MAX_PER_SECTION = 5;

function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

function matches(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.toLowerCase());
}

function useResults(trimmed: string): { results: Result[]; searchingContacts: boolean } {
  const { t } = useTranslation();
  const contactQuery = useDebounced(trimmed, CONTACT_DEBOUNCE_MS);
  const automations = useQuery({ queryKey: ['automations'], queryFn: automationsApi.list });
  const contacts = useQuery({
    queryKey: ['contacts', 'palette', contactQuery],
    queryFn: () => contactsApi.list({ q: contactQuery, limit: MAX_PER_SECTION }),
    enabled: contactQuery.length >= CONTACT_MIN_QUERY,
  });

  const results = useMemo<Result[]>(() => {
    // The first view lists the sidebar's pages whole (R40); typing also finds the settings pages.
    const candidates = trimmed ? ALL_PAGES : MAIN_PAGES;
    const pages: Result[] = candidates
      .filter((p) => matches(t(`nav.${p.label}`), trimmed) || (p.keywords ?? []).some((k) => matches(k, trimmed)))
      .map((p) => ({
        id: `page:${p.to}`,
        section: 'sectionPages',
        label: t(`nav.${p.label}`),
        hint: SETTINGS_PAGES.includes(p) ? t('nav.settings') : undefined,
        icon: p.icon,
        to: p.to,
        feature: p.feature,
      }));
    const flows: Result[] = trimmed
      ? (automations.data ?? [])
          .filter((a) => matches(a.name, trimmed))
          .slice(0, MAX_PER_SECTION)
          .map((a) => ({ id: `automation:${a.id}`, section: 'sectionAutomations', label: a.name, icon: 'workflow', to: `/automations/${a.id}` }))
      : [];
    const people: Result[] =
      contactQuery.length >= CONTACT_MIN_QUERY && contactQuery === trimmed
        ? (contacts.data?.data ?? []).map((c) => ({
            id: `contact:${c.id}`,
            section: 'sectionContacts',
            label: contactDisplayName(c),
            hint: c.email ?? c.username ?? undefined,
            icon: 'user',
            to: `/contacts/${c.id}`,
          }))
        : [];
    return [...pages, ...flows, ...people];
  }, [trimmed, contactQuery, automations.data, contacts.data, t]);

  const searchingContacts = trimmed.length >= CONTACT_MIN_QUERY && (contactQuery !== trimmed || contacts.isFetching);
  return { results, searchingContacts };
}

function CommandPalette({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const listId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const trimmed = query.trim();
  const { results, searchingContacts } = useResults(trimmed);

  useBodyScrollLock();
  useFocusTrap(dialogRef, inputRef);

  const optionId = (index: number) => `${listId}-option-${index}`;
  useEffect(() => setActiveIndex(0), [trimmed]);
  useEffect(() => {
    document.getElementById(`${listId}-option-${activeIndex}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, listId]);

  const go = (result: Result | undefined) => {
    if (!result) return;
    onClose();
    navigate(result.to);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (results.length === 0 ? 0 : (i + 1) % results.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (results.length === 0 ? 0 : (i - 1 + results.length) % results.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[activeIndex]);
    } else if (e.key === 'Escape') {
      // Only the palette closes: an open More sheet under it stays.
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  const status = searchingContacts
    ? t('palette.searchingContacts')
    : results.length === 0
      ? t('palette.noResults', { query: trimmed })
      : t('palette.resultCount', { count: results.length });

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[12vh]" role="presentation" onMouseDown={onClose}>
      <div className="absolute inset-0 animate-fade-in bg-[rgba(21,15,43,0.45)]" aria-hidden />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('palette.title')}
        onMouseDown={(e) => e.stopPropagation()}
        className="relative w-full max-w-[600px] animate-pop-in overflow-hidden rounded-(--radius-panel) bg-(--color-surface) shadow-modal"
      >
        <div className="flex items-center gap-2.5 border-b border-(--color-line) px-4">
          <Icon name="search" className="size-[18px] shrink-0 text-(--color-ink-500)" />
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={results[activeIndex] ? optionId(activeIndex) : undefined}
            aria-label={t('palette.inputLabel')}
            placeholder={t('palette.prompt')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            className="h-14 min-w-0 flex-1 bg-transparent text-[15px] text-(--color-ink-900) outline-none placeholder:text-(--color-ink-500)"
          />
          <kbd className="hidden shrink-0 rounded-md bg-(--color-surface-sunken) px-1.5 py-0.5 font-sans text-[11px] text-(--color-ink-500) sm:block">{t('palette.esc')}</kbd>
        </div>
        <div id={listId} role="listbox" aria-label={t('palette.results')} className="max-h-[min(560px,65vh)] overflow-y-auto p-2 empty:hidden">
          {SECTIONS.map((section) => {
            const members = results.map((result, index) => ({ result, index })).filter(({ result }) => result.section === section);
            if (members.length === 0) return null;
            const headingId = `${listId}-${section}`;
            return (
              <div key={section} role="group" aria-labelledby={headingId}>
                <p id={headingId} className="px-3 pb-1 pt-2.5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-(--color-ink-500)">
                  {t(`palette.${section}`)}
                </p>
                {members.map(({ result, index }) => {
                  const active = index === activeIndex;
                  return (
                    <div
                      key={result.id}
                      id={optionId(index)}
                      role="option"
                      aria-selected={active}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => go(result)}
                      className={`flex min-h-10 cursor-pointer items-center gap-3 rounded-lg px-3 text-[13.5px] ${
                        active ? 'bg-(--color-accent-wash-soft) text-(--color-ink-900)' : 'text-(--color-ink-700)'
                      }`}
                    >
                      <Icon name={result.icon} className={`size-4 shrink-0 ${active ? 'text-(--color-accent)' : 'text-(--color-ink-500)'}`} />
                      <span className="min-w-0 flex-1 truncate font-medium">{result.label}</span>
                      {result.feature ? <PlanBadge feature={result.feature} /> : null}
                      {result.hint ? <span className="shrink-0 truncate text-[12px] text-(--color-ink-500)">{result.hint}</span> : null}
                      {active ? (
                        <span aria-hidden className="shrink-0 text-[12px] text-(--color-ink-500)">
                          {t('palette.enter')}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        {/* Outside the listbox, which may only hold options: announced as the query changes. */}
        <p
          aria-live="polite"
          className={
            results.length === 0 && !searchingContacts
              ? 'px-3 py-8 text-center text-[13px] text-(--color-ink-500)'
              : searchingContacts
                ? 'px-5 pb-3 text-[12px] text-(--color-ink-500)'
                : 'sr-only'
          }
        >
          {status}
        </p>
        {/* The prompt promises automations and contacts: the footer says how to reach them (R40, A01-06). */}
        <p className={`${trimmed ? 'hidden sm:flex' : 'flex'} flex-wrap items-center gap-1.5 border-t border-(--color-line) bg-(--color-surface-sunken) px-4 py-2.5 text-[12px] text-(--color-ink-500)`}>
          {trimmed ? null : <span>{t('palette.typeToSearch')}</span>}
          <span className="hidden items-center gap-1.5 sm:inline-flex">
            {trimmed ? null : <span aria-hidden>·</span>}
            <kbd className="rounded-md bg-(--color-surface) px-1.5 py-0.5 font-sans text-[11px] ring-1 ring-(--color-line)">{t('palette.esc')}</kbd>
            {t('palette.toClose')}
          </span>
        </p>
      </div>
    </div>,
    document.body,
  );
}
